import { useEffect } from "react";
import { UtilsUseHistory } from "../utils/UtilsUseHistory";
import { useLoginOptionHooks } from "./login-option-hooks";
import { ApplicationState } from "../store";
import { LoginOptionState } from "../store/reducers/login-option-reducer";
import { useSelector } from "react-redux";
import { Cookie, LocalStorage } from "../services/storage";
import {
	COOKIE_GST_ATKN,
	EMAIL_LOGIN_VERIFICATION_RESULT,
	REDIRECT,
} from "../constants/storage";
import { LayoutState } from "../store/reducers/layout-reducer";
import { ROUTES } from "../configs/routes";
import { UserState } from "../store/reducers/user-reducer";
import { useAuth } from "./auth-hooks";
import { useEmailSupport } from "../utils/email-supported/useEmailSupport";
import { USER_TYPE } from "../types/user";

export const useEmailLoginHooks = () => {
	const {
		toggleLoginModal,
		toggleEmailLoginModal,
		toggleEmailOtpModal,
		removeGstToken,
		removeRedirectSession,
	} = useLoginOptionHooks();
	const { logout } = useAuth();
	const { isEmailLoginSupported } = useEmailSupport();
	const { isLoginModalOpen, isEmailLoginModalOpen, isEmailOtpModalOpen } =
		useSelector<ApplicationState, LoginOptionState>(
			(state) => state.loginOption
		);
	const { selectedCountry } = useSelector<ApplicationState, LayoutState>(
		(state) => state.layout
	);
	const { userCountryCode, userData } = useSelector<
		ApplicationState,
		UserState
	>((state) => state.user);
	const { go } = UtilsUseHistory();
	const isEmailLoginOngoing = isEmailLoginModalOpen || isEmailOtpModalOpen;
	const isLoginAttemptOngoing =
		isEmailLoginModalOpen || isEmailOtpModalOpen || isLoginModalOpen;

	/**
	 * Handles browser back button during email login flow.
	 * Closes all login modals and navigates back when user presses back button
	 * during an active email login session.
	 */
	useEffect(
		function handleBrowserBackDuringLogin() {
			window.onpopstate = () =>
				setTimeout(function () {
					if (isEmailLoginOngoing || isLoginModalOpen) {
						toggleEmailOtpModal(false);
						toggleLoginModal({ isLoginModalOpen: false });
						toggleEmailLoginModal(false);
						go(-1);
					}
				}, 0);
		},
		[isEmailLoginOngoing, isLoginModalOpen]
	);

	/**
	 * Cleans up email login tokens when verification is complete.
	 * Removes guest token and verification result from storage when
	 * email login modal closes and verification is no longer in progress.
	 */
	useEffect(
		function cleanupEmailLoginTokens() {
			const emailLoginVerificationResult = LocalStorage.get(
				EMAIL_LOGIN_VERIFICATION_RESULT
			);
			const isVerifying =
				emailLoginVerificationResult &&
				emailLoginVerificationResult === "false";

			if (isVerifying && !isEmailLoginOngoing) {
				removeGstToken();
			}

			if (Cookie.get(COOKIE_GST_ATKN) === undefined) {
				localStorage.removeItem(EMAIL_LOGIN_VERIFICATION_RESULT);
			}
		},
		// eslint-disable-next-line
		[isEmailLoginOngoing]
	);

	/**
	 * Cleans up redirect session when login attempt is complete.
	 * Removes any stored redirect session data after login flow ends.
	 */
	useEffect(
		function cleanupRedirectSessionAfterLogin() {
			if (!isLoginAttemptOngoing) {
				removeRedirectSession();
			}
		},
		[isLoginAttemptOngoing]
	);

	/**
	 * Validates email login support for user's selected country.
	 * Logs out user and clears email session if they switch to a country
	 * that doesn't support email login while logged in via email.
	 */
	useEffect(
		function validateEmailLoginCountrySupport() {
			if (!selectedCountry.code || !userCountryCode) return;
			// CHECK IF THE NEW selectedCountry doesn't support email login
			if (
				userData?.userType !== USER_TYPE.SA &&
				!isEmailLoginSupported(selectedCountry.code)
			) {
				// remove email session
				logout();
				removeGstToken();
				sessionStorage.setItem(REDIRECT, ROUTES.MYDATA);
			}
		},
		[userCountryCode, selectedCountry]
	);
};
